import { z } from 'zod';

import { loyaltyProgramDataSchema } from './booking';
import { PaymentDetailSchema } from '../booking/payment-detail';
import { makeEndpoint } from '../common';
import { PaymentMethods } from '../payment-method/public';
import { PersonSchema } from '../user/person';
import { WaitlistEntryActivitySchema } from '../waitlist/waitlist-entry-activity';

export enum BookingActivity {
  BOOKING_CREATED = 'booking-created',
  BOOKING_REFUNDED = 'booking-refunded',
  BOOKING_CANCELLED = 'booking-cancelled',
  BOOKING_VOIDED = 'booking-voided',
  BOOKING_RESCHEDULED = 'booking-rescheduled',
  BOOKING_MODIFIED = 'booking-modified',
}

const BaseBookingActivityResponseSchema = z.object({
  topic: z.nativeEnum(BookingActivity),
  issuer: PersonSchema,
  createdAt: z.string(),
});

export const BookingCreatedActivitySchema = BaseBookingActivityResponseSchema.extend({
  paymentMethod: z.nativeEnum(PaymentMethods),
  issuer: PersonSchema.nullable(),
  paymentDetail: PaymentDetailSchema.nullable(),
  loyaltyProgramData: loyaltyProgramDataSchema.optional(),
});

export type BookingCreatedActivity = z.infer<typeof BookingCreatedActivitySchema>;

export const BookingRefundedActivitySchema = BaseBookingActivityResponseSchema.extend({
  isGuestCancellation: z.boolean().default(false),
  refundedAmount: z.number(),
});

export type BookingRefundedActivity = z.infer<typeof BookingRefundedActivitySchema>;

export const BookingRescheduledActivitySchema = BaseBookingActivityResponseSchema.extend({
  isGuestRescheduled: z.boolean().default(false),
  previousDate: z.string(),
  newDate: z.string(),
  experienceTimezone: z.string().optional(),
  honoredPrice: z.boolean().optional(),
});

export type BookingRescheduledActivity = z.infer<typeof BookingRescheduledActivitySchema>;

export const BookingCancelledActivitySchema = BaseBookingActivityResponseSchema.extend({
  isGuestCancellation: z.boolean().default(false),
  participants: z.array(PersonSchema),
  bookingAddOnItems: z.array(
    z.object({
      label: z.string(),
      quantity: z.number(),
    }),
  ),
});

export type BookingCancelledActivity = z.infer<typeof BookingCancelledActivitySchema>;

export const BookingVoidedActivitySchema = BaseBookingActivityResponseSchema.extend({
  voidedAmount: z.number(),
});

export type BookingVoidedActivity = z.infer<typeof BookingVoidedActivitySchema>;

export const BookingModifiedActivitySchema = BaseBookingActivityResponseSchema.extend({
  previousExperiencePaidAmount: z.number().optional(),
  newExperiencePaidAmount: z.number().optional(),
  participantsAdded: z.array(
    z.object({
      priceTierName: z.string(),
      quantity: z.number(),
    }),
  ),
  addOnItemsAdded: z.array(
    z.object({
      label: z.string(),
      quantity: z.number(),
    }),
  ),
  priceDelta: z.number().optional(),
  shortenedByMinutes: z.number().int().positive().optional(),
  releasedAmount: z.number().nonnegative().optional(),
});

export type BookingModifiedActivity = z.infer<typeof BookingModifiedActivitySchema>;

export const BookingActivitySchema = z.union([
  BookingCreatedActivitySchema,
  BookingRefundedActivitySchema,
  BookingRescheduledActivitySchema,
  BookingCancelledActivitySchema,
  BookingVoidedActivitySchema,
  BookingModifiedActivitySchema,
]);

const BookingActivityResponseSchema = z.array(z.union([BookingActivitySchema, WaitlistEntryActivitySchema]));

export const BookingActivityEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ bookingId }: { bookingId: string }) => `v1/bookings/${bookingId}/activities`,
  requestSchema: z.void(),
  responseSchema: BookingActivityResponseSchema,
});
